import _ from 'lodash'
import exchangeStorageApi from '@/api/exchange-storage'
import { generateFormHandlerMutations } from '@/helpers/forms'
import {
  sectionPeriod, termField, toSizeString, toTerm,
} from '@/helpers/exchange-storage'

const emptyTerms = () => ({
  delivery: null,
  bakai: null,
  integration: null,
})

const state = {
  journals: [],
  lastRuns: [],

  currentMonth: null,
  loading: false,
  loaded: false,
  error: null,

  terms: emptyTerms(),
  saving: false,
  saveError: null,

  purging: false,

  purgeResult: null,
}

const errorText = (error, { failed, forbidden }) => {
  if (error.response?.status === 403) return forbidden

  return error.response?.data?.message || failed
}

const savedTerms = journals => journals.reduce(
  (terms, journal) => ({ ...terms, [termField(journal.key)]: journal.retentionMonths }),
  emptyTerms(),
)

const purgedText = ({ removedSections, freedBytes }, confirmed, journals) => {
  const partial = removedSections.length < confirmed

  if (!removedSections.length) {
    return {
      type: 'info',
      text: partial ? 'Удалять было нечего: секции уже не подпадали под срок' : 'Удалять было нечего',
      lines: [],
    }
  }

  const lines = _.map(_.groupBy(removedSections, 'journal'), (removed, key) => {
    const journal = journals.find(x => x.key === key)

    return `${journal ? journal.name : key}: ${removed.map(section => sectionPeriod(section)).join(', ')}`
  })
  const freed = toSizeString(freedBytes)

  return {
    type: partial ? 'info' : 'success',
    text: partial
      ? `Удалено секций: ${removedSections.length} из ${confirmed}: остальные уже не подпадали под срок. Освобождено ${freed}`
      : `Удалено секций: ${removedSections.length}, освобождено ${freed}`,
    lines,
  }
}

const getters = {}

const actions = {
  fetch({ commit }) {
    commit('LOADING')

    return exchangeStorageApi.get()
      .then(({ data }) => commit('SET_STORAGE', data))
      .catch(error => commit('SET_ERROR', errorText(error, {
        failed: 'Не удалось прочитать журналы обмена',
        forbidden: 'Недостаточно прав на просмотр хранения журналов',
      })))
  },

  save({ commit, dispatch }) {
    commit('SAVING', true)
    commit('SET_SAVE_ERROR', null)

    return exchangeStorageApi.updateSettings(_.mapValues(state.terms, toTerm))
      .then(({ data }) => {
        commit('SET_STORAGE', data)
        dispatch('message/success', 'Сроки хранения сохранены', { root: true })
      })
      .catch(error => commit('SET_SAVE_ERROR', errorText(error, {
        failed: 'Не удалось сохранить сроки хранения',
        forbidden: 'Недостаточно прав на изменение сроков хранения',
      })))
      .finally(() => commit('SAVING', false))
  },

  purge({ commit, dispatch }, sections) {
    commit('PURGING', true)
    commit('SET_PURGE_RESULT', null)

    return exchangeStorageApi.purge(sections)
      .then(({ data }) => commit('SET_PURGE_RESULT', purgedText(data, sections.length, state.journals)))
      .catch(error => commit('SET_PURGE_RESULT', {
        type: 'error',
        text: errorText(error, {
          failed: 'Не удалось очистить журналы',
          forbidden: 'Недостаточно прав на очистку журналов',
        }),
        lines: [],
      }))
      .finally(() => {
        commit('PURGING', false)
        dispatch('fetch')
      })
  },
}

const mutations = {
  LOADING(state) {
    state.loading = true
    state.error = null
  },

  SET_STORAGE(state, { journals, lastRuns, currentMonth }) {
    state.journals = journals || []
    state.lastRuns = lastRuns || []
    state.currentMonth = currentMonth
    state.terms = savedTerms(state.journals)
    state.saveError = null
    state.loading = false
    state.loaded = true
    state.error = null
  },

  SET_ERROR(state, text) {
    state.journals = []
    state.lastRuns = []
    state.currentMonth = null
    state.terms = emptyTerms()
    state.saveError = null
    state.loading = false
    state.loaded = false
    state.error = text
  },
  SAVING(state, value) {
    state.saving = value
  },
  SET_SAVE_ERROR(state, text) {
    state.saveError = text
  },
  PURGING(state, value) {
    state.purging = value
  },
  SET_PURGE_RESULT(state, result) {
    state.purgeResult = result
  },
  ...generateFormHandlerMutations(state),
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
